import axios, { type AxiosError, type AxiosInstance } from 'axios'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, loginRequest, tokenRequest } from '../auth/msalConfig'
import { recordApiError } from '../soporte'
import type {
  Oportunidad,
  OportunidadCreada,
  OportunidadInput,
  SolicitarDocumentoInput,
  SolicitarDocumentoResult,
  BulkResult,
  BulkPreview,
  UserProfile,
  ApiKey,
  ApiKeyCreated,
  ReglaValidador,
  SeveridadRegla,
  ServicioConectado,
  EntidadReceptora,
  Proveedor,
  ProveedorInput,
  EjecutivoProveedor,
  EjecutivoProveedorInput,
  ProveedorDetalle,
  DocumentoCFDI,
  DocumentoBandeja,
  AccionManualResult,
  ComparacionResponse,
  KpisResponse,
  LinkEstado,
  PublicMetadata,
  PublicUploadResult,
  PlantillaMensaje,
  PlantillaMensajeInput,
  PlantillaMensajeUpdate,
  PlantillaPreview,
  GuiaInfo,
  SugerenciaCierre,
  CambioEstadoCierreResult,
  EstadoCierre,
  TipoDocumentoCFDI,
  TipoOperacion,
  ComplementoPendiente,
  RecordatorioMasivoResult,
  CambioAccesoResultado,
  MiembroEquipo,
  MiembroEquipoInput,
  ResponsableResultado,
} from '../types/portal'

// Base URL para TODAS las llamadas al backend. En dev cae a '/api' y Vite
// lo proxea a localhost:8002; en prod VITE_API_BASE_URL apunta al Container
// App absoluto. Las llamadas a `fetch()` DEBEN prefijar con API_BASE_URL.
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

// El router público del backend NO vive bajo /api (es sin auth por diseño).
// En dev, Vite proxea /public → :8002. En prod, derivamos del API_BASE_URL
// sustituyendo el sufijo /api por /public; si el operador ya pasó una URL
// sin /api, el replace es no-op y ajustamos manualmente vía VITE_PUBLIC_BASE_URL.
export const PUBLIC_BASE_URL = (
  import.meta.env.VITE_PUBLIC_BASE_URL ?? API_BASE_URL.replace(/\/api$/, '/public')
).replace(/\/$/, '') || '/public'

// Cada llamada fallida queda en el buffer del reporte de problemas (método,
// ruta sin ids, estado y X-Request-ID), para que el ticket apunte al log.
function registrarFallas(cliente: AxiosInstance): void {
  cliente.interceptors.response.use(undefined, (error: AxiosError) => {
    const detail = (error.response?.data as { detail?: { error?: unknown; code?: unknown } })
      ?.detail
    const code = typeof detail?.error === 'string' ? detail.error : detail?.code
    recordApiError({
      method: error.config?.method ?? 'GET',
      path: `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`,
      status: error.response?.status ?? 0,
      code: typeof code === 'string' ? code : null,
      requestId: (error.response?.headers?.['x-request-id'] as string | undefined) ?? null,
    })
    return Promise.reject(error)
  })
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
})
registrarFallas(api)

// Attach Bearer token to every axios request
api.interceptors.request.use(async (config) => {
  const { Authorization } = await authHeaders()
  if (Authorization) config.headers.Authorization = Authorization
  return config
})

// Auth headers para axios y para el reporte de problemas (kit de soporte, con
// fetch). Si la sesión de Microsoft caducó, redirige al login en lugar de mandar
// la llamada sin token.
export async function authHeaders(): Promise<Record<string, string>> {
  const account = msalInstance.getAllAccounts()[0]
  if (!account) return {}
  try {
    const result = await msalInstance.acquireTokenSilent({ ...tokenRequest, account })
    return { Authorization: `Bearer ${result.accessToken}` }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account })
    }
    return {}
  }
}

// ── Auth ────────────────────────────────────────────────────────────────────

export const getMe = async (): Promise<UserProfile> => {
  const { data } = await api.get('/auth/me')
  return data
}

// ── Oportunidades ───────────────────────────────────────────────────────────

export const listOportunidades = async (params?: {
  tipo_origen?: string
  rfc_proveedor?: string
  numero_contrato?: string
  estado_cierre?: string[]
  limit?: number
  offset?: number
}): Promise<Oportunidad[]> => {
  const { data } = await api.get('/oportunidades', {
    params,
    paramsSerializer: { indexes: null },
  })
  return data
}

export const getOportunidad = async (id: number): Promise<Oportunidad> => {
  const { data } = await api.get(`/oportunidades/${id}`)
  return data
}

export const listOportunidadHermanas = async (id: number): Promise<Oportunidad[]> => {
  const { data } = await api.get(`/oportunidades/${id}/hermanas`)
  return data
}

export const subirOcFirmada = async (
  oportunidadId: number,
  file: File,
): Promise<DocumentoCFDI> => {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post(`/oportunidades/${oportunidadId}/oc-firmada`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// ── Orden de Compra Pandadoc (Chunk JJ-06) ─────────────────────────────────

export const getOrdenCompra = async (
  oportunidadId: number,
): Promise<import('../types/portal').OrdenCompra | null> => {
  try {
    const { data } = await api.get(`/oportunidades/${oportunidadId}/orden-compra`)
    return data
  } catch (err: unknown) {
    // 404 = aún no hay OC para esta op; el caller renderiza estado "pendiente".
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 404) return null
    throw err
  }
}

export const ordenCompraPdfUrl = (oportunidadId: number): string => {
  return `${API_BASE_URL}/oportunidades/${oportunidadId}/orden-compra/pdf`
}

// Dispara manualmente el envío de la OC a firma electrónica (WeeTrust).
export const enviarOcAFirma = async (
  oportunidadId: number,
): Promise<import('../types/portal').OrdenCompra> => {
  const { data } = await api.post(`/oportunidades/${oportunidadId}/orden-compra/enviar`)
  return data
}

export const getFirmantesOc = async (
  oportunidadId: number,
): Promise<import('../types/portal').FirmanteOc[]> => {
  const { data } = await api.get(`/oportunidades/${oportunidadId}/orden-compra/firmantes`)
  return data
}

// Reenvía por correo la liga del MISMO documento de firma (no crea otro) a los
// firmantes elegidos que aún no firman. Devuelve a quién se mandó.
export const reenviarLigaOc = async (
  oportunidadId: number,
  roles: import('../types/portal').RolFirmanteOc[],
): Promise<{ enviados: string[] }> => {
  const { data } = await api.post(`/oportunidades/${oportunidadId}/orden-compra/reenviar`, { roles })
  return data
}

// ── Previsualización de documentos (PDF inline + XML legible) ──────────────

// Baja el contenido del documento como Blob (con auth Bearer) para previsualizar
// inline sin forzar descarga. Un `<iframe src>` crudo apuntando al endpoint NO
// lleva el header Authorization (el token MSAL vive en localStorage, no en
// cookie) → el backend responde 401. Bajamos el blob por axios y lo mostramos
// con un object URL (mismo patrón que `fetchDocumentoDistribuidorBlob`).
export async function fetchDocumentoBlob(
  id: number,
  formato: 'pdf' | 'xml' = 'pdf',
): Promise<Blob> {
  const r = await api.get(`/documentos/${id}/contenido`, {
    params: { formato, disposition: 'inline' },
    responseType: 'blob',
  })
  return r.data as Blob
}

export interface CfdiConcepto {
  descripcion: string
  cantidad: string
  valor_unitario: string
  importe: string
}

export interface CfdiPreview {
  documento_id: number
  tipo: string
  uuid: string
  rfc_emisor: string
  rfc_receptor: string
  importe: string
  fecha_emision: string | null
  metodo_pago: string | null
  estado: string
  tiene_xml: boolean
  emisor_nombre: string | null
  receptor_nombre: string | null
  subtotal: string | null
  moneda: string | null
  conceptos: CfdiConcepto[]
}

export const getCfdiPreview = async (id: number): Promise<CfdiPreview> => {
  const { data } = await api.get(`/documentos/${id}/preview-xml`)
  return data
}

// ── Lotes de pago (Cuentas por Pagar) ──────────────────────────────────────

export interface DocumentoElegible {
  documento_id: number
  tipo: string
  uuid: string
  importe: string
  fecha_emision: string
  tiene_xml: boolean
  tiene_pdf: boolean
  oportunidad_id: number
  numero_contrato: string
  proveedor_rfc: string
  proveedor_razon_social: string
  nombre_cliente: string | null
  etapa: string
}

export interface LotePago {
  id: number
  creado_por: string | null
  estado: string
  formato: string
  total_documentos: number
  total_importe: string
  notas: string | null
  created_at: string
  generado_at: string | null
}

export const listLotesPagoElegibles = async (params?: {
  q?: string
  rfc_proveedor?: string
  limit?: number
}): Promise<DocumentoElegible[]> => {
  const { data } = await api.get('/lotes-pago/elegibles', { params })
  return data
}

export const crearLotePago = async (body: {
  documento_cfdi_ids: number[]
  formato: 'xml' | 'xml_pdf'
  notas?: string | null
}): Promise<LotePago> => {
  const { data } = await api.post('/lotes-pago', body)
  return data
}

export const listLotesPago = async (): Promise<LotePago[]> => {
  const { data } = await api.get('/lotes-pago')
  return data
}

// Descarga el ZIP del lote (blob → click sintético).
export async function descargarLoteZip(loteId: number): Promise<void> {
  const r = await api.get(`/lotes-pago/${loteId}/zip`, { responseType: 'blob' })
  const cd = (r.headers as Record<string, string>)['content-disposition'] || ''
  const match = /filename="?([^"]+)"?/.exec(cd)
  const filename = match?.[1] ?? `lote_pago_${loteId}.zip`
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── Estado de la operación (Sprint 5) ──────────────────────────────────────

export const getSugerenciaCierre = async (
  oportunidadId: number,
): Promise<SugerenciaCierre> => {
  const { data } = await api.get(`/oportunidades/${oportunidadId}/sugerencia-cierre`)
  return data
}

export const cambiarEstadoCierre = async (
  oportunidadId: number,
  estado_cierre: EstadoCierre,
  motivo: string,
): Promise<CambioEstadoCierreResult> => {
  const { data } = await api.patch(`/oportunidades/${oportunidadId}/estado-cierre`, {
    estado_cierre,
    motivo,
  })
  return data
}

// ── Complementos pendientes (Sprint 6) ─────────────────────────────────────

export const listComplementosPendientes = async (params?: {
  antiguedad_min_dias?: number
  rfc_proveedor?: string
  tipo_operacion?: string
  estado_cierre?: string
  limit?: number
  offset?: number
}): Promise<ComplementoPendiente[]> => {
  const { data } = await api.get('/complementos-pendientes', { params })
  return data
}

export const enviarRecordatorioMasivo = async (
  oportunidad_ids: number[],
  plantilla_codigo = 'recordatorio_cp',
): Promise<RecordatorioMasivoResult> => {
  const { data } = await api.post('/complementos-pendientes/recordatorio-masivo', {
    oportunidad_ids,
    plantilla_codigo,
  })
  return data
}

export const createOportunidad = async (body: OportunidadInput): Promise<OportunidadCreada> => {
  const { data } = await api.post('/oportunidades', body)
  return data
}

export const uploadOportunidadesBulk = async (file: File): Promise<BulkResult> => {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/oportunidades/bulk', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// Dry-run: previsualiza el archivo (valida sin crear) para el flujo del mockup.
export const previewOportunidadesBulk = async (file: File): Promise<BulkPreview> => {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/oportunidades/bulk/preview', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// Descarga la plantilla .xlsx (blob con auth) para la carga masiva.
export async function descargarPlantillaBulk(): Promise<void> {
  const r = await api.get('/oportunidades/bulk/plantilla', { responseType: 'blob' })
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'plantilla_oportunidades.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const solicitarDocumento = async (
  oportunidadId: number,
  body: SolicitarDocumentoInput,
): Promise<SolicitarDocumentoResult> => {
  const { data } = await api.post(`/oportunidades/${oportunidadId}/solicitar-documento`, body)
  return data
}

export const listDocumentosOportunidad = async (
  oportunidadId: number,
): Promise<DocumentoCFDI[]> => {
  const { data } = await api.get(`/oportunidades/${oportunidadId}/documentos`)
  return data
}

export const getLinkEstado = async (oportunidadId: number): Promise<LinkEstado> => {
  const { data } = await api.get(`/oportunidades/${oportunidadId}/link`)
  return data
}

// ── Documentos (bandeja revisión) ──────────────────────────────────────────

export const listDocumentosBandeja = async (params?: {
  estado?: string[]
  tipo?: string[]
  estado_cierre?: string[]
  q?: string
  limit?: number
  offset?: number
}): Promise<DocumentoBandeja[]> => {
  const { data } = await api.get('/documentos', {
    params,
    paramsSerializer: { indexes: null }, // ?estado=a&estado=b en lugar de estado[]=
  })
  return data
}

export const aprobarDocumento = async (
  id: number,
  motivo: string,
): Promise<AccionManualResult> => {
  const { data } = await api.post(`/documentos/${id}/aprobar-manual`, { motivo })
  return data
}

export const rechazarDocumento = async (
  id: number,
  motivo: string,
): Promise<AccionManualResult> => {
  const { data } = await api.post(`/documentos/${id}/rechazar`, { motivo })
  return data
}

/** Comparación CFDI-vs-OC campo-a-campo + chequeos reales del validador. */
export const getComparacionDocumento = async (id: number): Promise<ComparacionResponse> => {
  const { data } = await api.get(`/documentos/${id}/comparacion`)
  return data
}

export async function descargarDocumento(id: number, formato: 'xml' | 'pdf'): Promise<void> {
  const r = await api.get(`/documentos/${id}/contenido`, {
    params: { formato },
    responseType: 'blob',
  })
  // El nombre viene del Content-Disposition; si no, generamos uno mínimo.
  const cd = (r.headers as Record<string, string>)['content-disposition'] || ''
  const match = /filename="?([^"]+)"?/.exec(cd)
  const filename = match?.[1] ?? `documento_${id}.${formato}`
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── KPIs ───────────────────────────────────────────────────────────────────

export const getKpis = async (dias = 30): Promise<KpisResponse> => {
  const { data } = await api.get('/kpis', { params: { dias } })
  return data
}

// ── Admin ──────────────────────────────────────────────────────────────────

export const listApiKeys = async (): Promise<ApiKey[]> => {
  const { data } = await api.get('/admin/api-keys')
  return data
}

export const createApiKey = async (
  body: { nombre: string; scopes?: string[]; expira_en?: string | null },
): Promise<ApiKeyCreated> => {
  const { data } = await api.post('/admin/api-keys', body)
  return data
}

export const revokeApiKey = async (id: number): Promise<void> => {
  await api.post(`/admin/api-keys/${id}/revoke`)
}

// ── Validador: reglas on/off (tab Integraciones) ──────────────────────────
export const listReglasValidador = async (): Promise<ReglaValidador[]> => {
  const { data } = await api.get('/admin/validador/reglas')
  return data
}

export const toggleReglaValidador = async (
  clave: string,
  activa: boolean,
): Promise<ReglaValidador> => {
  const { data } = await api.patch(`/admin/validador/reglas/${clave}`, { activa })
  return data
}

/** Cambia la dureza de una regla sin apagarla. Cadena vacía = restaurar la del
 *  código. */
export const setSeveridadRegla = async (
  clave: string,
  severidad: SeveridadRegla | '',
): Promise<ReglaValidador> => {
  const { data } = await api.patch(`/admin/validador/reglas/${clave}`, { severidad })
  return data
}

// ── Servicios conectados (tab Integraciones) ──────────────────────────────
export const listServiciosConectados = async (): Promise<ServicioConectado[]> => {
  const { data } = await api.get('/admin/integraciones/servicios')
  return data
}

export const listEntidadesReceptoras = async (): Promise<EntidadReceptora[]> => {
  const { data } = await api.get('/admin/entidades-receptoras')
  return data
}

export const createEntidadReceptora = async (
  body: { rfc: string; razon_social: string; activa?: boolean },
): Promise<EntidadReceptora> => {
  const { data } = await api.post('/admin/entidades-receptoras', body)
  return data
}

export const updateEntidadReceptora = async (
  id: number,
  body: { razon_social?: string; activa?: boolean },
): Promise<EntidadReceptora> => {
  const { data } = await api.patch(`/admin/entidades-receptoras/${id}`, body)
  return data
}

export const listProveedores = async (): Promise<Proveedor[]> => {
  const { data } = await api.get('/admin/proveedores')
  return data
}

export const createProveedor = async (body: ProveedorInput): Promise<Proveedor> => {
  const { data } = await api.post('/admin/proveedores', body)
  return data
}

export const getProveedorDetalle = async (
  proveedorId: number,
): Promise<ProveedorDetalle> => {
  const { data } = await api.get(`/admin/proveedores/${proveedorId}/detalle`)
  return data
}

export const updateProveedorDatos = async (
  proveedorId: number,
  body: {
    razon_social: string
    nombre_comercial?: string | null
    email_contacto: string | null
  },
): Promise<Proveedor> => {
  const { data } = await api.put(`/admin/proveedores/${proveedorId}`, body)
  return data
}

export const listEjecutivosProveedor = async (
  proveedorId: number,
): Promise<EjecutivoProveedor[]> => {
  const { data } = await api.get(`/admin/proveedores/${proveedorId}/ejecutivos`)
  return data
}

export const createEjecutivoProveedor = async (
  proveedorId: number,
  body: EjecutivoProveedorInput,
): Promise<EjecutivoProveedor> => {
  const { data } = await api.post(`/admin/proveedores/${proveedorId}/ejecutivos`, body)
  return data
}

export const setProveedorActivo = async (
  id: number,
  activo: boolean,
): Promise<Proveedor> => {
  const { data } = await api.patch(`/admin/proveedores/${id}`, { activo })
  return data
}

export const setEjecutivoActivo = async (
  proveedorId: number,
  ejecutivoId: number,
  activo: boolean,
): Promise<EjecutivoProveedor> => {
  const { data } = await api.patch(
    `/admin/proveedores/${proveedorId}/ejecutivos/${ejecutivoId}`,
    { activo },
  )
  return data
}

/** Edición completa del ejecutivo (nombre, correo, teléfono, activo, principal). */
export const updateEjecutivoProveedor = async (
  proveedorId: number,
  ejecutivoId: number,
  body: EjecutivoProveedorInput,
): Promise<EjecutivoProveedor> => {
  const { data } = await api.put(
    `/admin/proveedores/${proveedorId}/ejecutivos/${ejecutivoId}`,
    body,
  )
  return data
}

/** Elimina el ejecutivo. El llamador ofrece "Deshacer" recreándolo. */
export const deleteEjecutivoProveedor = async (
  proveedorId: number,
  ejecutivoId: number,
): Promise<void> => {
  await api.delete(`/admin/proveedores/${proveedorId}/ejecutivos/${ejecutivoId}`)
}

// ── Portal del proveedor — auth con cookie de sesión (Chunk HH) ────────────

// Cliente axios para los endpoints del portal del proveedor. Usa cookies de
// sesión HTTP-only emitidas por /api/portal/auth/verify. NO lleva Bearer
// token de Entra ID (es para el proveedor, no para el operador interno).
const portalApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
  withCredentials: true, // Esencial para que la cookie portal_session viaje.
})
registrarFallas(portalApi)

export interface PortalLoginResponse {
  mensaje: string
  vida_min: number
}

export interface PortalMe {
  ejecutivo_proveedor_id: number
  nombre: string
  email: string
  /** Proveedor del ejecutivo (header del portal). Aditivo. */
  proveedor_rfc?: string | null
  proveedor_razon_social?: string | null
  /** Contacto principal de un proveedor activo: ve "Mi equipo". Lo decide el backend. */
  puede_gestionar_equipo?: boolean
}

export const portalAuthLogin = async (
  email: string,
  rfc: string,
): Promise<PortalLoginResponse> => {
  const { data } = await portalApi.post('/portal/auth/login', { email, rfc })
  return data
}

export const portalAuthVerify = async (
  email: string,
  rfc: string,
  codigo: string,
): Promise<PortalMe> => {
  const { data } = await portalApi.post('/portal/auth/verify', { email, rfc, codigo })
  return data
}

export const portalAuthLogout = async (): Promise<void> => {
  await portalApi.post('/portal/auth/logout')
}

export const portalAuthMe = async (): Promise<PortalMe> => {
  const { data } = await portalApi.get('/portal/auth/me')
  return data
}

// ── Portal expediente — operaciones del proveedor logueado (Chunk HH-05) ───

export interface PortalOportunidad {
  id: number
  numero_contrato: string
  proveedor_razon_social: string
  nombre_cliente: string | null
  descripcion_activo: string | null
  importe_esperado: string
  moneda: string
  secuencia_distribuidor: number
  tipo_operacion: TipoOperacion
  estado_cierre: EstadoCierre
  tipo_doc_solicitado: TipoDocumentoCFDI | null
  activo_hasta: string | null
  ventana_abierta: boolean
  /** Ejecutivo del proveedor que da seguimiento (todo el equipo ve la operación). */
  responsable_id?: number | null
  responsable_nombre?: string | null
}

export const listMisOportunidades = async (): Promise<PortalOportunidad[]> => {
  const { data } = await portalApi.get('/portal/oportunidades')
  return data
}

export const getMetadataDeOportunidad = async (
  oportunidadId: number,
): Promise<PublicMetadata> => {
  const { data } = await portalApi.get(`/portal/expediente/${oportunidadId}/metadata`)
  return data
}

export const subirCfdiAOportunidad = async (
  oportunidadId: number,
  xmlFile: File,
  pdfFile: File,
): Promise<PublicUploadResult> => {
  const form = new FormData()
  form.append('xml_file', xmlFile)
  form.append('pdf_file', pdfFile)
  const { data } = await portalApi.post(
    `/portal/expediente/${oportunidadId}/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

// ── Portal: "Mi equipo" (solo el contacto principal; el backend responde 403 al resto) ──

export const listarMiEquipo = async (): Promise<MiembroEquipo[]> => {
  const { data } = await portalApi.get('/portal/equipo')
  return data
}

export const agregarMiembroEquipo = async (body: MiembroEquipoInput): Promise<MiembroEquipo> => {
  const { data } = await portalApi.post('/portal/equipo', body)
  return data
}

/** Solo nombre y teléfono: el correo no se puede cambiar desde el portal. */
export const editarMiembroEquipo = async (
  ejecutivoId: number,
  body: { nombre?: string; telefono?: string | null },
): Promise<MiembroEquipo> => {
  const { data } = await portalApi.patch(`/portal/equipo/${ejecutivoId}`, body)
  return data
}

/** Quita o devuelve el acceso. Si es responsable de operaciones en curso, el
 *  backend responde 409 `requiere_reasignacion` salvo que se confirme
 *  `reasignarOperaciones` (pasan al principal). */
export const cambiarAccesoMiembro = async (
  ejecutivoId: number,
  activo: boolean,
  reasignarOperaciones = false,
): Promise<CambioAccesoResultado> => {
  const { data } = await portalApi.post(`/portal/equipo/${ejecutivoId}/acceso`, {
    activo,
    reasignar_operaciones: reasignarOperaciones,
  })
  return data
}

export const reasignarResponsable = async (
  oportunidadId: number,
  ejecutivoId: number,
): Promise<ResponsableResultado> => {
  const { data } = await portalApi.post(
    `/portal/equipo/oportunidades/${oportunidadId}/responsable`,
    { ejecutivo_proveedor_id: ejecutivoId },
  )
  return data
}

// Descarga el reporte PDF (branding LeaseMD) de los complementos seleccionados.
export async function descargarReporteComplementos(oportunidadIds: number[]): Promise<void> {
  const r = await api.post(
    '/complementos-pendientes/reporte',
    { oportunidad_ids: oportunidadIds },
    { responseType: 'blob' },
  )
  const cd: string = (r.headers['content-disposition'] as string) ?? ''
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd)
  const filename = m ? decodeURIComponent(m[1]) : 'complementos-pendientes.pdf'
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// Descarga (attachment) un documento del expediente del portal. Baja el blob con
// la cookie de sesión (portalApi) y dispara la descarga con nombre legible.
export async function descargarDocumentoPortal(
  oportunidadId: number,
  documentoId: number,
  formato: 'xml' | 'pdf' = 'pdf',
): Promise<void> {
  const r = await portalApi.get(
    `/portal/expediente/${oportunidadId}/documentos/${documentoId}/contenido`,
    { params: { formato }, responseType: 'blob' },
  )
  const cd: string = (r.headers['content-disposition'] as string) ?? ''
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd)
  const filename = m ? decodeURIComponent(m[1]) : `documento.${formato}`
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// URL absoluta a la guía pública. Útil para anchors en el portal público.
export const publicGuiaUrl = (): string => `${PUBLIC_BASE_URL}/guia-facturacion.pdf`

// Baja la guía pública como Blob (mismo patrón que `fetchDocumentoBlob`) para
// previsualizarla vía object URL. Un `<iframe src>` apuntando a la URL fija
// puede servir una respuesta cacheada con `Content-Disposition: attachment` → el
// navegador DESCARGA en vez de renderizar. El object URL de un blob siempre
// renderiza inline, y `params.t` evita servir la respuesta cacheada.
export async function fetchGuiaBlob(): Promise<Blob> {
  const r = await api.get('/guia-facturacion.pdf', {
    baseURL: PUBLIC_BASE_URL,
    responseType: 'blob',
    params: { t: Date.now() },
  })
  return r.data as Blob
}

// ── Plantillas ─────────────────────────────────────────────────────────────

export const listPlantillas = async (params?: {
  activa?: boolean
}): Promise<PlantillaMensaje[]> => {
  const { data } = await api.get('/admin/plantillas', { params })
  return data
}

export const getPlantilla = async (codigo: string): Promise<PlantillaMensaje> => {
  const { data } = await api.get(`/admin/plantillas/${codigo}`)
  return data
}

export const createPlantilla = async (
  body: PlantillaMensajeInput,
): Promise<PlantillaMensaje> => {
  const { data } = await api.post('/admin/plantillas', body)
  return data
}

export const updatePlantilla = async (
  codigo: string,
  body: PlantillaMensajeUpdate,
): Promise<PlantillaMensaje> => {
  const { data } = await api.patch(`/admin/plantillas/${codigo}`, body)
  return data
}

export const previewPlantilla = async (
  codigo: string,
  contexto: Record<string, unknown>,
): Promise<PlantillaPreview> => {
  const { data } = await api.post(`/admin/plantillas/${codigo}/preview`, { contexto })
  return data
}

// ── Guía PDF ───────────────────────────────────────────────────────────────

export const getGuiaInfo = async (): Promise<GuiaInfo> => {
  const { data } = await api.get('/admin/guia-facturacion/info')
  return data
}

export const replaceGuia = async (file: File): Promise<GuiaInfo> => {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/admin/guia-facturacion', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// ── Distribuidor — alta pública (Chunk GG) ─────────────────────────────────

// Cliente axios sin auth para el endpoint público de distribuidores.
// El endpoint vive bajo /api/public/distribuidores (no bajo /public como
// el portal del proveedor con token).
const publicDistApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120_000,
})

export interface DocumentoSlot {
  tipo_doc: string
  file: File
}

export interface RegistroDistribuidorPayload {
  tipo_distribuidor: 'autos' | 'equipo_medico'
  tipo_persona: 'fisica' | 'moral'
  rfc: string
  nombre_comercial: string
  instalaciones: 'propias' | 'rentadas'
  cofepris_requerido?: 'si' | 'no' | null
  cofepris_motivo?: string | null
  // Info primaria + visita: null para autos (esos pasos solo aplican a equipo
  // médico — ajuste vendors 2026-07-03).
  antiguedad_descripcion: string | null
  canal_ventas: string | null
  sitio_web: string | null
  redes_sociales: string | null
  tipos_activos: string | null
  condicion_equipos: 'nuevos' | 'seminuevos' | 'ambos' | null
  ubicacion_fiscal_texto: string | null
  ubicacion_fiscal_lat: number | null
  ubicacion_fiscal_lng: number | null
  estructura_organizacional: string | null
  marcas_representadas: string | null
  proceso_postventa: string | null
  visita_contacto_nombre: string | null
  visita_contacto_telefono: string | null
  visita_contacto_email: string | null
  visita_fecha_hora: string | null // ISO 8601
  visita_ubicacion_texto: string | null
  visita_ubicacion_lat: number | null
  visita_ubicacion_lng: number | null
  contactos_clave?: Array<{
    area: 'representante_legal' | 'contabilidad' | 'comercial' | 'facturacion'
    nombre: string
    telefono?: string | null
    email?: string | null
  }>
  referencias_marca?: Array<{
    nombre_marca: string
    cargo_contacto?: string | null
    empresa?: string | null
    telefono?: string | null
  }>
}

export interface RegistroDistribuidorResultado {
  id: number
  rfc: string
  estado: string
  nombre_comercial: string
  documentos_recibidos: number
  siguiente_paso: string
}

export const enviarRegistroDistribuidor = async (
  payload: RegistroDistribuidorPayload,
  documentos: DocumentoSlot[],
  turnstileToken: string,
  resumeToken?: string | null,
): Promise<RegistroDistribuidorResultado> => {
  const form = new FormData()
  form.append(
    'payload',
    JSON.stringify({
      ...payload,
      tipos_doc: documentos.map((d) => d.tipo_doc),
    }),
  )
  form.append('turnstile_token', turnstileToken)
  if (resumeToken) form.append('resume_token', resumeToken)
  for (const d of documentos) {
    form.append('files', d.file, d.file.name)
  }
  const { data } = await publicDistApi.post(
    '/public/distribuidores/registro',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export interface ConfigDistribuidor {
  documentos: Array<{
    tipo_doc: string
    reglas: Array<{
      tipo_distribuidor?: string[]
      tipo_persona?: string[]
      instalaciones?: string[]
      cofepris_requerido?: string[]
    }>
  }>
  tipos_distribuidor: string[]
  cofepris_opciones: string[]
}

/** Catálogo de documentos + reglas (fuente única backend) para el wizard. */
export const fetchConfigDistribuidor = async (): Promise<ConfigDistribuidor> => {
  const { data } = await publicDistApi.get('/public/distribuidores/config')
  return data
}

export interface BorradorOut {
  id: number
  rfc: string
  expires_at: string
  resume_token: string
}

export interface BorradorDocumento {
  tipo_doc: string
  filename_original: string
  size_bytes: number
  sha256: string
}

export const guardarBorradorDistribuidor = async (input: {
  rfc: string
  tipo_persona: 'fisica' | 'moral'
  email_contacto: string
  payload: Record<string, unknown>
  documentos?: DocumentoSlot[]
  resumeToken?: string | null
}): Promise<BorradorOut> => {
  const form = new FormData()
  const docs = input.documentos ?? []
  form.append(
    'payload',
    JSON.stringify({
      rfc: input.rfc,
      tipo_persona: input.tipo_persona,
      email_contacto: input.email_contacto,
      payload: input.payload,
      tipos_doc: docs.map((d) => d.tipo_doc),
    }),
  )
  if (input.resumeToken) form.append('resume_token', input.resumeToken)
  for (const d of docs) {
    form.append('files', d.file, d.file.name)
  }
  const { data } = await publicDistApi.post(
    '/public/distribuidores/borrador',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export interface BorradorRecuperado {
  id: number
  rfc: string
  email_contacto: string
  expires_at: string
  payload: Record<string, unknown>
  documentos: BorradorDocumento[]
}

export const cargarBorradorDistribuidor = async (
  resumeToken: string,
): Promise<BorradorRecuperado> => {
  const { data } = await publicDistApi.get(
    `/public/distribuidores/borrador/${resumeToken}`,
  )
  return data
}

// ── Distribuidor — admin (Chunk GG08) ───────────────────────────────────────

export type EstadoRegistroDistribuidor =
  | 'borrador'
  | 'enviado'
  | 'en_revision'
  | 'requiere_correccion'
  | 'aprobado'
  | 'rechazado'

export interface RegistroDistribuidorAdmin {
  id: number
  rfc: string
  tipo_persona: 'fisica' | 'moral'
  /** Giro del distribuidor — subtítulo del mockup ('autos' | 'equipo_medico'). */
  tipo_distribuidor: string
  /** Nombre de marca, capturado por el proveedor en el alta. */
  nombre_comercial: string
  /** Denominación fiscal, leída de la CSF o capturada por el revisor. Null
   *  mientras no se conozca; al aprobar se cae al nombre comercial. */
  razon_social: string | null
  instalaciones: string
  estado: EstadoRegistroDistribuidor
  motivo_rechazo: string | null
  proveedor_id: number | null
  monday_item_id: string | null
  visita_contacto_nombre: string
  visita_contacto_email: string
  visita_fecha_hora: string
  created_at: string
  aprobado_por: string | null
  aprobado_at: string | null
  // (otros campos textuales se piden en /detalle)
  [extra: string]: unknown
}

export interface DocumentoDistribuidorAdmin {
  id: number
  tipo_doc: string
  filename_original: string
  blob_path: string
  sha256: string
  size_bytes: number
  av_status: 'pending' | 'clean' | 'infected' | 'skipped'
  av_engine: string | null
  av_scanned_at: string | null
  av_signature: string | null
  monday_asset_id: string | null
  ia_status: 'pending' | 'ok' | 'observacion' | 'error' | null
  ia_validacion: {
    status?: string
    observaciones?: string[]
    campos_extraidos?: Record<string, unknown>
    confidence?: number
    error?: string | null
  } | null
  ia_validated_at: string | null
  created_at: string
}

export interface ContactoDistribuidorAdmin {
  id: number
  area: 'representante_legal' | 'contabilidad' | 'comercial' | 'facturacion'
  nombre: string
  telefono: string | null
  email: string | null
}

export interface ReferenciaMarcaAdmin {
  id: number
  nombre_marca: string
  cargo_contacto: string | null
  empresa: string | null
  telefono: string | null
}

export interface ObservacionRegistroAdmin {
  id: number
  campo: string | null
  observacion: string
  creada_por: string | null
  resuelta: boolean
  created_at: string
  resuelta_at: string | null
}

export interface RegistroDistribuidorDetalle {
  registro: RegistroDistribuidorAdmin
  documentos: DocumentoDistribuidorAdmin[]
  proveedor: {
    id: number
    rfc: string
    razon_social: string
    nombre_comercial: string | null
    activo: boolean
  } | null
  contactos?: ContactoDistribuidorAdmin[]
  referencias?: ReferenciaMarcaAdmin[]
  observaciones?: ObservacionRegistroAdmin[]
}

/** Captura o corrige la razón social fiscal del alta, antes de aprobarla. */
export const actualizarRazonSocialRegistro = async (
  registroId: number,
  razon_social: string,
): Promise<RegistroDistribuidorAdmin> => {
  const { data } = await api.patch(
    `/v1/distribuidores/${registroId}/razon-social`,
    { razon_social },
  )
  return data
}

export const observarRegistroDistribuidor = async (
  registroId: number,
  body: { campo?: string | null; observacion: string },
): Promise<ObservacionRegistroAdmin> => {
  const { data } = await api.post(`/v1/distribuidores/${registroId}/observar`, body)
  return data
}

export const resolverObservacionDistribuidor = async (
  registroId: number,
  obsId: number,
): Promise<ObservacionRegistroAdmin> => {
  const { data } = await api.post(
    `/v1/distribuidores/${registroId}/observaciones/${obsId}/resolver`,
  )
  return data
}

export const listarRegistrosDistribuidor = async (params?: {
  estado?: EstadoRegistroDistribuidor
  rfc?: string
}): Promise<RegistroDistribuidorAdmin[]> => {
  const { data } = await api.get('/v1/distribuidores', { params })
  return data
}

export const obtenerRegistroDistribuidor = async (
  id: number,
): Promise<RegistroDistribuidorDetalle> => {
  const { data } = await api.get(`/v1/distribuidores/${id}`)
  return data
}

export const aprobarRegistroDistribuidor = async (
  id: number,
): Promise<{
  registro_id: number
  proveedor_id: number
  proveedor_rfc: string
  aprobado_por: string
  aprobado_at: string
}> => {
  const { data } = await api.post(`/v1/distribuidores/${id}/aprobar`)
  return data
}

export const rechazarRegistroDistribuidor = async (
  id: number,
  motivo: string,
): Promise<RegistroDistribuidorAdmin> => {
  const { data } = await api.post(`/v1/distribuidores/${id}/rechazar`, { motivo })
  return data
}

export const urlDocumentoDistribuidor = (registroId: number, docId: number): string =>
  `${API_BASE_URL}/v1/distribuidores/${registroId}/documentos/${docId}/contenido`

// Descarga el PDF de un documento de distribuidor como Blob, vía el `api` con
// interceptor Bearer. El endpoint exige rol (require_alianzas) y un <iframe src>
// crudo NO manda el header Authorization → 401 en prod. Por eso bajamos el blob
// autenticado y lo previsualizamos con un object URL (mismo patrón que contract-ai).
export async function fetchDocumentoDistribuidorBlob(
  registroId: number,
  docId: number,
): Promise<Blob> {
  const r = await api.get(
    `/v1/distribuidores/${registroId}/documentos/${docId}/contenido`,
    { responseType: 'blob' },
  )
  return r.data as Blob
}

// Descarga el XLSX formato Cobra (visita operativa) para subir a Kobra.
export async function descargarVisitaXlsx(registroId: number): Promise<Blob> {
  const r = await api.post(
    `/v1/distribuidores/${registroId}/generar-visita-xlsx`,
    undefined,
    { responseType: 'blob' },
  )
  return r.data as Blob
}

// Descarga el paquete MÍNIMO para Kobra (XLSX Cobra + INE + comprobante + resumen).
export async function descargarExpedienteKobraZip(registroId: number): Promise<Blob> {
  const r = await api.post(
    `/v1/distribuidores/${registroId}/expediente-zip`,
    undefined,
    { responseType: 'blob' },
  )
  return r.data as Blob
}

export async function descargarExpedienteCompletoZip(registroId: number): Promise<Blob> {
  const r = await api.post(
    `/v1/distribuidores/${registroId}/expediente-completo-zip`,
    undefined,
    { responseType: 'blob' },
  )
  return r.data as Blob
}

// ── Corrección de alta (Fase B) ─────────────────────────────────────────────

// Admin (auth): pide correcciones; el backend envía el correo con el link y
// devuelve el registro actualizado (NO el token, que va en el correo).
export const solicitarCorreccionDistribuidor = async (
  id: number,
  mensaje: string,
  tipos_doc: string[],
): Promise<RegistroDistribuidorAdmin> => {
  const { data } = await api.post(`/v1/distribuidores/${id}/solicitar-correccion`, {
    mensaje,
    tipos_doc,
  })
  return data
}

export interface CorreccionInfo {
  registro_id: number
  nombre_comercial: string
  rfc: string
  mensaje: string
  tipos_doc_solicitados: string[]
  expires_at: string
}

// Público (sin auth, por token): resuelve la solicitud y reenvía documentos.
export const cargarCorreccion = async (token: string): Promise<CorreccionInfo> => {
  const { data } = await publicDistApi.get(`/public/distribuidores/correccion/${token}`)
  return data
}

export const enviarCorreccion = async (
  token: string,
  docs: { tipo_doc: string; file: File }[],
): Promise<RegistroDistribuidorAdmin> => {
  const form = new FormData()
  form.append('tipos_doc', JSON.stringify(docs.map((d) => d.tipo_doc)))
  for (const d of docs) form.append('files', d.file, d.file.name)
  const { data } = await publicDistApi.post(
    `/public/distribuidores/correccion/${token}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export default api

// ── Notificaciones internas ───────────────────────────────────────────────

export interface NotificacionInterna {
  id: number
  audiencia_roles: string[]
  tipo: string
  titulo: string
  mensaje: string
  link: string | null
  entidad_tipo: string | null
  entidad_id: number | null
  leida: boolean
  created_at: string
  updated_at: string
}

export const listNotificaciones = async (
  params?: { limit?: number; offset?: number },
): Promise<NotificacionInterna[]> => {
  const { data } = await api.get('/v1/notificaciones', { params })
  return data
}

export const getUnreadCount = async (): Promise<number> => {
  const { data } = await api.get('/v1/notificaciones/unread-count')
  return data.unread as number
}

export const marcarNotificacionLeida = async (id: number): Promise<void> => {
  await api.post(`/v1/notificaciones/${id}/leer`)
}

export const marcarTodasNotificacionesLeidas = async (): Promise<void> => {
  await api.post('/v1/notificaciones/leer-todas')
}
